import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { combineLatest, map, Observable, ReplaySubject } from 'rxjs';
import { startWith } from 'rxjs/operators';

import {
  FinnairBoundItem,
  FinnairDisruptedBoundItem,
  FinnairItineraryItemFlight,
  FinnairLocation,
} from '@fcom/dapi/api/models';
import { Airport, ChipTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { capitalize } from '@fcom/utils';

import {
  isBound,
  isByBusOnly,
  isFlight,
  isUsingSAF,
  legEndpointToUfoAirport,
  operatingAirlineCodeOrBusIndicator,
} from '../../../utils/common-booking.utils';
import { FinnairBoundItemWithLocation } from '../../../interfaces';

@Component({
  selector: 'fin-flight-review-card-common',
  styleUrls: ['./flight-review-card-common.component.scss'],
  templateUrl: './flight-review-card-common.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightReviewCardCommonComponent implements OnInit, OnChanges {
  readonly ChipTheme = ChipTheme;

  @Input()
  flightOrBound:
    | FinnairBoundItemWithLocation
    | FinnairBoundItem
    | FinnairDisruptedBoundItem
    | FinnairItineraryItemFlight;
  @Input()
  locations?: { [key: string]: FinnairLocation };
  @Input()
  title: string;
  @Input()
  viewDetailsClosed: string;
  @Input()
  hideModify = false;
  @Input()
  hideViewDetails = false;
  // Parent component passes down info if the component is used in booking flow or not
  // We want to show the sustainability feature in booking flow, but not in cancel or reacc flows
  @Input() paxReviewCard = false;

  @Output()
  viewDetails: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  modifyClicked: EventEmitter<void> = new EventEmitter<void>();

  ufoAirports$: Observable<{ departure: Airport; arrival: Airport }>;
  tails: string[];
  isByBusOnly: boolean;
  isFlightUsingSAF: boolean;
  stops: number;
  fareFamily: string;
  uniqueAirlineNames: string[];

  flightOrBound$: ReplaySubject<
    FinnairBoundItemWithLocation | FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
  > = new ReplaySubject<
    FinnairBoundItemWithLocation | FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
  >(1);
  locations$: ReplaySubject<{ [key: string]: FinnairLocation }> = new ReplaySubject<{ [key: string]: FinnairLocation }>(
    1
  );

  constructor(private languageService: LanguageService) {}

  ngOnInit(): void {
    this.ufoAirports$ = combineLatest([
      this.languageService.translate('date'),
      this.flightOrBound$,
      this.locations$.pipe(startWith(undefined)),
    ]).pipe(
      map(([translations, flightOrBound, locations]) => ({
        departure: legEndpointToUfoAirport(flightOrBound.departure, translations, locations),
        arrival: legEndpointToUfoAirport(flightOrBound.arrival, translations, locations),
      }))
    );
    this.locations$.next(this.locations);
    this.updateFlightOrBound();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations']) {
      this.locations$.next(this.locations);
    }

    if (changes['flightOrBound']) {
      this.updateFlightOrBound();
    }
  }

  private updateFlightOrBound(): void {
    this.flightOrBound$.next(this.flightOrBound);

    if (isBound(this.flightOrBound)) {
      this.uniqueAirlineNames = this.flightOrBound.uniqueAirlineNames;
      this.stops = this.flightOrBound.stops;
      this.fareFamily =
        this.flightOrBound.fareFamily?.name ?? capitalize((this.flightOrBound as FinnairBoundItem).cabinClass ?? '');
      this.tails = this.flightOrBound.itinerary
        .filter(isFlight)
        .map((flight) => operatingAirlineCodeOrBusIndicator(flight));
    } else {
      this.uniqueAirlineNames = [this.flightOrBound.operatingAirline.name];
      this.stops = 0;
      this.fareFamily = this.flightOrBound.cabinClass;
      this.tails = [operatingAirlineCodeOrBusIndicator(this.flightOrBound)];
    }

    this.isByBusOnly = isByBusOnly(this.flightOrBound);

    this.isFlightUsingSAF = isUsingSAF(this.flightOrBound);
  }
}
